import React, { createRef } from 'react';
import { ModalService } from '@philips-internal/oswald-ui-components';
import { ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { AuthenticationService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { isContentfulString } from '@philips-internal/oswald-ui-utils';
import { MultiFactorAuthentication, MultiFactorAuthenticationActivation, MultiFactorAuthenticationDeactivation } from '../components/index.js';
const MODAL_ID = 'oswald-mfa-modal';
function createMultiFactorAuthenticationService() {
    function validateAuthentication(code, token, storeToken = true) {
        return AuthenticationService.validateMultiFactorAuthentication({ code, storeToken, token });
    }
    function activateAuthentication(code) {
        return AuthenticationService.activateMultiFactorAuthentication({ code });
    }
    function requestNewAuthenticationCode() {
        return AuthenticationService.requestNewMultiFactorAuthenticationCode();
    }
    function handleErrorResponse(options) {
        const { error, ref, rejectionCallback } = options;
        if (!error.response) {
            return rejectionCallback(error);
        }
        void AuthenticationService.getErrorMessageFromResponse(error.response)
            .then((message) => {
            if (isContentfulString(message)) {
                ref.current?.setErrorMessage(message);
            }
            else {
                rejectionCallback(error);
            }
        })
            .catch(() => rejectionCallback(error));
    }
    const close = () => {
        ModalService.close(MODAL_ID);
    };
    const open = (props) => {
        return new Promise((resolve, reject) => {
            const multiFactorAuthenticationRef = createRef();
            let rejected = false;
            const closeAndReject = (error) => {
                rejected = true;
                close();
                reject(error);
            };
            const onModalClose = () => {
                if (rejected) {
                    return;
                }
                reject();
            };
            const onResendCode = () => {
                void requestNewAuthenticationCode();
            };
            const onValidationSuccess = (response) => {
                resolve(response);
                close();
            };
            const onValidationError = (error) => {
                handleErrorResponse({
                    error,
                    ref: multiFactorAuthenticationRef,
                    rejectionCallback: closeAndReject,
                });
            };
            const onSendCode = (code) => {
                void validateAuthentication(code, props.token, props.storeToken)
                    .then((response) => onValidationSuccess(response))
                    .catch((error) => onValidationError(error));
            };
            const configuration = {
                children: (React.createElement(MultiFactorAuthentication, { codeSize: props.codeSize, continueExpression: props.continueExpression, inputCodeExpression: props.inputCodeExpression, onResendCode: onResendCode, onSendCode: onSendCode, ref: multiFactorAuthenticationRef, resendCodeExpression: props.resendCodeExpression })),
                closeable: props.closeable ?? false,
                id: MODAL_ID,
                onClose: onModalClose,
                theme: ThemeTonalRange.ULTRA_LIGHT,
            };
            ModalService.openDialog(configuration);
        });
    };
    const activate = (props) => {
        return new Promise((resolve, reject) => {
            const multiFactorAuthenticationRef = createRef();
            let rejected = false;
            const closeAndReject = (error) => {
                rejected = true;
                close();
                reject(error);
            };
            const onModalClose = () => {
                if (rejected) {
                    return;
                }
                reject();
            };
            const onValidationSuccess = () => {
                resolve();
                close();
            };
            const onValidationError = (error) => {
                handleErrorResponse({
                    error,
                    ref: multiFactorAuthenticationRef,
                    rejectionCallback: closeAndReject,
                });
            };
            const onSendCode = (code) => {
                void activateAuthentication(code)
                    .then(() => onValidationSuccess())
                    .catch((error) => onValidationError(error));
            };
            const onRequestActivationSuccess = (response) => {
                const configuration = {
                    children: (React.createElement(MultiFactorAuthenticationActivation, { activationExpression: props.activationExpression, codeSize: props.codeSize, continueExpression: props.continueExpression, inputCodeExpression: props.inputCodeExpression, link: response.link, onSendCode: onSendCode, ref: multiFactorAuthenticationRef, secret: response.secret })),
                    closeable: props.closeable ?? false,
                    id: MODAL_ID,
                    onClose: onModalClose,
                    theme: ThemeTonalRange.ULTRA_LIGHT,
                };
                ModalService.openDialog(configuration);
            };
            void AuthenticationService.requestMultiFactorAuthenticationActivation()
                .then((response) => onRequestActivationSuccess(response))
                .catch((error) => reject(error));
        });
    };
    const deactivate = (props) => {
        return new Promise((resolve, reject) => {
            const multiFactorAuthenticationRef = createRef();
            let rejected = false;
            const closeAndReject = (error) => {
                rejected = true;
                close();
                reject(error);
            };
            const onModalClose = () => {
                if (rejected) {
                    return;
                }
                reject();
            };
            const onDeactivationSuccess = () => {
                resolve();
                close();
            };
            const onDeactivationError = (error) => {
                handleErrorResponse({
                    error,
                    ref: multiFactorAuthenticationRef,
                    rejectionCallback: closeAndReject,
                });
            };
            const onSendCode = (code) => {
                const params = { code };
                void AuthenticationService.deactivateMultiFactorAuthentication(params)
                    .then(() => onDeactivationSuccess())
                    .catch((error) => onDeactivationError(error));
            };
            const configuration = {
                children: (React.createElement(MultiFactorAuthenticationDeactivation, { codeSize: props.codeSize, continueExpression: props.continueExpression, inputCodeExpression: props.inputCodeExpression, onSendCode: onSendCode, ref: multiFactorAuthenticationRef })),
                closeable: props.closeable ?? false,
                id: MODAL_ID,
                onClose: onModalClose,
                theme: ThemeTonalRange.ULTRA_LIGHT,
            };
            ModalService.openDialog(configuration);
        });
    };
    return {
        activate,
        close,
        deactivate,
        open,
    };
}
const MultiFactorAuthenticationModalService = createMultiFactorAuthenticationService();
export { MultiFactorAuthenticationModalService, };
//# sourceMappingURL=multi-factor-authentication-service.js.map